import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError, getPaymentMethods, postCheckout } from '../api/client'
import type { CheckoutRequest, Fulfilment, VoucherStatus } from '../api/types'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Field } from '../components/Field'
import { Gutscheinfeld } from '../components/Gutscheinfeld'
import { Rabattfeld } from '../components/Rabattfeld'
import { Wahl, Wahlgruppe } from '../components/Wahlgruppe'
import { Fehlerhinweis, Ladehinweis } from '../components/States'
import { useAuth } from '../auth'
import { LAENDER, useLandName } from '../laender'
import { useCart, zeilenname } from '../cart'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { useT, type Textschluessel } from '../i18n'
import { usePriceFormat, useShopConfig } from '../shopConfig'
import { useShopPage } from '../shopPages'

/**
 * Die Kasse — drei Schritte und eine Übersicht, die stehen bleibt.
 *
 * Vorher war sie eine lange Seite. Das war nicht falsch, aber es war lang: Auf
 * 390 Pixeln standen sieben Abschnitte untereinander, und wer bei der
 * Lieferadresse war, sah weder, was er bestellt, noch, was es kostet. Genau an
 * dieser Stelle brechen Einkäufe ab.
 *
 * Jetzt: Kontakt → Versand → Zahlung, mit einer Anzeige oben, die sagt, wie
 * viel noch kommt — und rechts durchgehend die Bestellübersicht. Das ist der
 * Aufbau, den jeder Kunde von anderen Läden kennt, und die Erwartung bringt er
 * mit, sobald er „Zur Kasse" drückt.
 *
 * Die Eingaben liegen in *einem* Zustand, nicht je Schritt: Vor und zurück
 * kostet deshalb nichts. Ein Schritt geht erst weiter, wenn seine eigenen
 * Felder stimmen — geprüft wird mit derselben Funktion wie vorher, nur je
 * Schritt gefiltert. Zwei Prüfungen für dieselben Felder wären zwei, von denen
 * eine irgendwann abweicht.
 *
 * Der Versandschritt entfällt bei Abholung ganz, samt Adressfeldern: Es gibt
 * dann keine Anschrift, an die etwas ginge. Deshalb steht die Wahl zwischen
 * Versand und Abholung im *ersten* Schritt — sie entscheidet, was danach
 * überhaupt kommt.
 *
 * Was hier **nicht** steht: Preise, die der Browser rechnet. Der Server nimmt
 * seinen eigenen Warenkorb und rechnet die Versandkosten — beides aus dem Rumpf
 * zu schicken hieße, den Kunden über den Preis entscheiden zu lassen.
 */
type Feldname =
  | 'email'
  | 'name'
  | 'phone'
  | 'empfaenger'
  | 'firma'
  | 'strasse'
  | 'zusatz'
  | 'plz'
  | 'ort'

type Schritt = 'kontakt' | 'versand' | 'zahlung'

/**
 * Welches Feld zu welchem Schritt gehört.
 *
 * Eine Liste und keine Bedingung an jedem Feld: Sie entscheidet zugleich,
 * welche Fehler ein „Weiter" aufhalten und ab wann sie überhaupt zu sehen sind.
 * Stünde das an zwei Stellen, ließe sich ein Schritt mit einem Fehler
 * verlassen, den er selbst gar nicht anzeigt.
 */
const FELDER: Record<Schritt, Feldname[]> = {
  kontakt: ['email', 'name', 'phone'],
  versand: ['empfaenger', 'firma', 'strasse', 'zusatz', 'plz', 'ort'],
  zahlung: [],
}

const SCHRITTNAME: Record<Schritt, Textschluessel> = {
  kontakt: 'kasse.schrittKontakt',
  versand: 'kasse.schrittVersand',
  zahlung: 'kasse.schrittZahlung',
}

function schrittVon(feld: Feldname): Schritt {
  return FELDER.versand.includes(feld) ? 'versand' : 'kontakt'
}

interface Formular {
  email: string
  name: string
  phone: string
  empfaenger: string
  firma: string
  strasse: string
  zusatz: string
  plz: string
  ort: string
  note: string
}

const LEER: Formular = {
  email: '',
  name: '',
  phone: '',
  empfaenger: '',
  firma: '',
  strasse: '',
  zusatz: '',
  plz: '',
  ort: '',
  note: '',
}

/**
 * Prüft die Pflichtangaben.
 *
 * Absichtlich nachsichtig bei der E-Mail: Eine strenge Prüfung weist mehr
 * gültige Adressen ab, als sie falsche findet. Geprüft wird nur, was sicher
 * falsch ist — kein @, nichts davor, nichts dahinter.
 */
function pruefe(
  formular: Formular,
  versand: boolean,
  land: string,
  t: (schluessel: Textschluessel) => string,
): Partial<Record<Feldname, string>> {
  const fehler: Partial<Record<Feldname, string>> = {}

  const email = formular.email.trim()
  if (!email) {
    fehler.email = t('kasse.fehlerEmailLeer')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    fehler.email = t('kasse.fehlerEmailForm')
  }

  if (!formular.name.trim()) {
    fehler.name = t('kasse.fehlerName')
  }

  if (versand) {
    if (!formular.strasse.trim()) {
      fehler.strasse = t('kasse.fehlerStrasse')
    }
    const plz = formular.plz.trim()
    if (!plz) {
      fehler.plz = t('kasse.fehlerPlzLeer')
    } else if (land === 'DE' && !/^\d{5}$/.test(plz)) {
      fehler.plz = t('kasse.fehlerPlzForm')
    }
    if (!formular.ort.trim()) {
      fehler.ort = t('kasse.fehlerOrt')
    }
  }

  return fehler
}

export function CheckoutPage() {
  const t = useT()
  const { shopName, primaryHost } = useShopConfig()
  const { ich } = useAuth()
  const {
    ansicht,
    laedt,
    arbeitet,
    fehler: korbfehler,
    gekuerzt,
    zustellart,
    land,
    setzeZustellung,
    neuLaden,
  } = useCart()
  const preis = usePriceFormat()
  // „widerruf" ist das Handle, unter dem der Server die Pflichtseite anlegt
  // (bl/shop/page.go). Gibt es sie nicht, fehlt der Verweis — und dem Laden
  // sagt sein Backoffice, dass ihm eine Pflichtseite fehlt.
  const widerruf = useShopPage('widerruf')
  const landName = useLandName()
  const navigate = useNavigate()

  const [formular, setFormular] = useState<Formular>(LEER)
  const [beruehrt, setBeruehrt] = useState<Set<Feldname>>(new Set())
  /** Welche Schritte schon einmal abgeschickt wurden — ab dann zeigen sie ihre Fehler. */
  const [versucht, setVersucht] = useState<Set<Schritt>>(new Set())
  const [schritt, setSchritt] = useState<Schritt>('kontakt')
  const [sendet, setSendet] = useState(false)
  const [serverfehler, setServerfehler] = useState<string | null>(null)
  const [zahlungsart, setZahlungsart] = useState('')
  /**
   * Die Wertgutscheine, mit denen bezahlt werden soll.
   *
   * Hier und nicht im Warenkorb: Ein Gutschein mindert keinen Preis, er
   * bezahlt. Gezogen wird er erst beim Bestellen, in derselben Transaktion —
   * sonst verlöre jemand, der die Kasse wieder verlässt, sein Guthaben an eine
   * Bestellung, die es nie gab.
   */
  const [gutscheine, setGutscheine] = useState<VoucherStatus[]>([])
  /**
   * Ob die Übersicht am Telefon aufgeklappt ist.
   *
   * Zugeklappt beim Öffnen: Auf 390 Pixeln will man zuerst das Formular sehen
   * — aber wissen, was man zahlt, und deshalb steht der Betrag im Knopf. Ab
   * 900 Pixeln entscheidet allein das Stylesheet; dort ist sie immer offen und
   * der Knopf verschwindet.
   */
  const [uebersichtOffen, setUebersichtOffen] = useState(false)

  /**
   * Die Sperre gegen den zweiten Klick.
   *
   * Ein Zustand allein genügt hier nicht: Zwischen Klick und neuem Rendern
   * liegt ein Wimpernschlag, in dem ein zweiter Klick durchkommt — und zwei
   * Bestellungen sind zwei Rechnungen, zwei Pakete und ein verärgerter Kunde.
   * Die Referenz steht sofort.
   */
  const laeuft = useRef(false)

  useSeo({
    title: t('kasse.seoTitel', { laden: shopName }),
    description: t('kasse.seoText'),
    canonicalHost: primaryHost,
  })

  // Wer angemeldet ist, hat Name und E-Mail schon einmal getippt. Nur einmal
  // und nur in leere Felder: Ein Nachtragen bei jedem Rendern überschriebe,
  // was der Kunde gerade ändert.
  const uebernommen = useRef(false)
  useEffect(() => {
    if (uebernommen.current || !ich.signedIn) return
    uebernommen.current = true
    setFormular((vorher) => ({
      ...vorher,
      email: vorher.email || (ich.email ?? ''),
      name: vorher.name || (ich.name ?? ''),
    }))
  }, [ich])

  // Die Zahlungsarten hängen an der Zustellart: Barzahlung bei Abholung
  // verschwindet, sobald sich jemand etwas schicken lässt.
  const { data: arten } = useResource(
    (signal) => getPaymentMethods(zustellart, signal),
    [zustellart],
  )

  useEffect(() => {
    if (!arten) return
    // Die gewählte Art beibehalten, solange es sie noch gibt — sonst die
    // erste. Der Server sortiert sie, und oben steht, was am schnellsten zu
    // Geld führt.
    setZahlungsart((vorher) =>
      arten.some((art) => art.key === vorher) ? vorher : (arten[0]?.key ?? ''),
    )
  }, [arten])

  const versand = zustellart === 'shipping'
  const fehler = pruefe(formular, versand, land, t)

  // Bei Abholung gibt es den mittleren Schritt nicht. Die Liste entsteht bei
  // jedem Rendern neu und ist damit immer die, die gerade gilt — ein
  // gespeicherter Index hinge sonst irgendwann neben einem Schritt, den es
  // nicht mehr gibt.
  const schritte: Schritt[] = versand ? ['kontakt', 'versand', 'zahlung'] : ['kontakt', 'zahlung']
  const aktiv: Schritt = schritte.includes(schritt) ? schritt : 'kontakt'
  const index = schritte.indexOf(aktiv)
  const letzter = index === schritte.length - 1

  /** Was in diesem Schritt noch fehlt — in der Reihenfolge der Felder. */
  function offeneFelder(welcher: Schritt): Feldname[] {
    return FELDER[welcher].filter((feld) => fehler[feld])
  }

  /** Fehler erst zeigen, wenn das Feld verlassen oder der Schritt abgeschickt wurde. */
  function sichtbar(feld: Feldname): string | null {
    if (!versucht.has(schrittVon(feld)) && !beruehrt.has(feld)) return null
    return fehler[feld] ?? null
  }

  function setzeFeld<K extends keyof Formular>(feld: K, wert: Formular[K]) {
    setFormular((vorher) => ({ ...vorher, [feld]: wert }))
  }

  function beruehre(feld: Feldname) {
    setBeruehrt((vorher) => new Set(vorher).add(feld))
  }

  /**
   * Springt zum ersten beanstandeten Feld.
   *
   * Gesucht wird über die Kennung, nicht über `aria-invalid`: Das Attribut
   * steht erst nach dem nächsten Rendern da, der Sprung soll aber sofort
   * passieren. Beim Schrittwechsel muss er allerdings warten, bis der neue
   * Schritt gezeichnet ist — vorher gibt es das Feld im Baum gar nicht.
   */
  function springeZu(feld: Feldname, spaeter = false) {
    const springen = () => document.getElementById(feld)?.focus()
    if (spaeter) window.setTimeout(springen, 0)
    else springen()
  }

  function weiter() {
    setVersucht((vorher) => new Set(vorher).add(aktiv))
    const offen = offeneFelder(aktiv)
    if (offen.length > 0) {
      springeZu(offen[0]!)
      return
    }
    setSchritt(schritte[index + 1] ?? aktiv)
    // Nach oben: Der nächste Schritt fängt oben an, und der Kunde steht sonst
    // mitten in ihm — auf dem Telefon sogar unterhalb seiner Überschrift.
    window.scrollTo({ top: 0 })
  }

  function zurueck() {
    setSchritt(schritte[index - 1] ?? aktiv)
    window.scrollTo({ top: 0 })
  }

  // Was so nicht bestellt werden kann. Die Sätze in `blockers` kommen fertig
  // vom Server; verschwundene Artikel sieht das Ladenfenster selbst.
  const unbestellbar = ansicht.lines.some((zeile) => zeile.unavailable)
  const gesperrt = ansicht.blockers.length > 0 || unbestellbar

  async function bestellen(ereignis: FormEvent) {
    ereignis.preventDefault()
    setServerfehler(null)

    // Die Eingabetaste in einem Feld schickt das Formular ab — auch im ersten
    // Schritt. Dort heißt das „Weiter" und nicht „bestellen": Eine Bestellung,
    // die entsteht, weil jemand nach der Postleitzahl die Eingabetaste
    // gedrückt hat, ist keine bewusste Bestellung.
    if (!letzter) {
      weiter()
      return
    }

    if (laeuft.current) return

    // Auf dem letzten Schritt wird alles geprüft, nicht nur er selbst: Sonst
    // ließe sich mit der Eingabetaste an einer Pflichtangabe vorbeibestellen.
    setVersucht(new Set(schritte))
    for (const welcher of schritte) {
      const offen = offeneFelder(welcher)
      if (offen.length === 0) continue
      setSchritt(welcher)
      springeZu(offen[0]!, welcher !== aktiv)
      return
    }

    if (ansicht.lines.length === 0 || gesperrt) return

    laeuft.current = true
    setSendet(true)

    const anschrift = {
      name: formular.empfaenger.trim() || formular.name.trim(),
      company: formular.firma.trim(),
      street: formular.strasse.trim(),
      extra: formular.zusatz.trim(),
      postalCode: formular.plz.trim(),
      city: formular.ort.trim(),
      country: land,
    }

    const daten: CheckoutRequest = {
      // Keine Positionen: Der Server nimmt seinen eigenen Warenkorb.
      email: formular.email.trim(),
      name: formular.name.trim(),
      phone: formular.phone.trim(),
      fulfilment: zustellart,
      note: formular.note.trim(),
      // Bei Abholung gibt es keine Anschrift, an die etwas ginge — ein leeres
      // Adressfeld mitzuschicken erfände eine, die niemand geprüft hat.
      ...(versand ? { address: anschrift } : {}),
      // Nur die Codes. Wie viel davon gezogen wird, rechnet der Server aus dem
      // Guthaben und dem, was zu zahlen ist — der Browser bestimmt es nicht.
      ...(gutscheine.length > 0 ? { voucherCodes: gutscheine.map((g) => g.code) } : {}),
    }

    try {
      const { bestellung, token } = await postCheckout(daten)
      // Der Server hat den Warenkorb aufgebraucht; hier steht noch die alte
      // Ansicht. Ohne dieses Nachladen trüge das Symbol in der Kopfzeile
      // weiter eine Zahl für Ware, die schon bestellt ist.
      neuLaden()
      // Der Schlüssel steht in der Adresse und nicht nur im Zustand des
      // Browsers.
      //
      // Das ist der Unterschied zwischen einer Bestellung, die ein Neuladen
      // überlebt, und einer, die dabei verschwindet — und damit auch der
      // Unterschied zwischen einem Bezahlweg, der zurückführt, und einem, der
      // den Kunden auf einem leeren Suchformular absetzt. Solange die
      // Bestellung nur im Zustand lag, musste dem Kunden gesagt werden, er
      // möge sich die Nummer merken.
      const ziel =
        `/bestellung/${encodeURIComponent(bestellung.number)}` +
        (token ? `?token=${encodeURIComponent(token)}` : '')
      // `replace`, damit „Zurück" nicht auf eine Kasse mit leerem Warenkorb
      // führt — und schon gar nicht zu einem zweiten Absenden.
      navigate(ziel, {
        replace: true,
        // Der Zustand bleibt als Abkürzung: Er spart den ersten Abruf. Nötig
        // ist er nicht mehr.
        state: { bestellung, email: daten.email, zahlungsart },
      })
    } catch (ausnahme) {
      // Was der Server sagt, sagt er meist genauer als wir: „Von Blaues
      // T-Shirt sind noch 2 verfügbar" nennt die Zeile, die zu ändern ist.
      setServerfehler(
        ausnahme instanceof ApiError && ausnahme.status !== 0 && ausnahme.status < 500
          ? // Unverändert: Der Server nennt die Zeile und die Zahl, siehe i18n.tsx.
            ausnahme.message
          : t('kasse.fehlerAllgemein'),
      )
      // Der Warenkorb kann sich geändert haben, während die Bestellung
      // unterwegs war — etwa, weil der Letzte eines Artikels weg ist.
      neuLaden()
      laeuft.current = false
      setSendet(false)
    }
  }

  const kopf = (
    <>
      <Breadcrumbs pfade={[{ name: t('nav.startseite'), pfad: '/' }, { name: t('kasse.titel') }]} />
      <h1>{t('kasse.titel')}</h1>
    </>
  )

  if (laedt) {
    return (
      <>
        {kopf}
        <Ladehinweis text={t('korb.laedt')} />
      </>
    )
  }

  if (ansicht.lines.length === 0) {
    return (
      <>
        {kopf}
        {korbfehler != null ? (
          <Fehlerhinweis fehler={korbfehler} />
        ) : (
          <p className="hinweis">
            {t('kasse.korbLeer')} <Link to="/suche">{t('kasse.zumSortiment')}</Link>.
          </p>
        )}
      </>
    )
  }

  return (
    <>
      {kopf}

      <div className="kasse-raster">
        {/*
          Die Übersicht steht im Quelltext *vor* dem Formular und landet am
          Telefon damit oben — genau dort, wo Shopify sie hat, und aus dem
          richtigen Grund: Wer zur Kasse geht, will zuerst sehen, was er zahlt.
          Zusammengeklappt, weil er danach das Formular ausfüllen will und nicht
          erst an fünf Zeilen vorbeirollen. Ab 900 Pixeln schiebt das Raster sie
          nach rechts, wo sie durchgehend offen steht.
        */}
        <aside className="kasse-uebersicht" data-offen={uebersichtOffen}>
          <button
            className="kasse-uebersicht-knopf"
            type="button"
            aria-expanded={uebersichtOffen}
            aria-controls="kasse-uebersicht-inhalt"
            onClick={() => setUebersichtOffen((vorher) => !vorher)}
          >
            <span>
              {t('kasse.uebersicht')}
              <svg
                className="kasse-uebersicht-pfeil"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m6 9 6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {/* Der Betrag steht im Knopf und nicht nur dahinter: Ihn zu kennen
                ist der einzige Grund, aus dem jemand die Übersicht aufklappt. */}
            <strong>{preis(ansicht.totalGross)}</strong>
            <span className="nur-fuer-screenreader">
              {uebersichtOffen ? t('kasse.uebersichtAusblenden') : t('kasse.uebersichtEinblenden')}
            </span>
          </button>

          <div className="kasse-uebersicht-inhalt" id="kasse-uebersicht-inhalt">
            <h2 className="kasse-uebersicht-titel">{t('kasse.uebersicht')}</h2>

            <ul className="uebersicht">
              {ansicht.lines.map((zeile) => (
                <li key={zeile.lineId || zeile.variantId}>
                  <span>
                    {zeile.quantity} × {zeilenname(zeile, t('korb.unbekannterArtikel'))}
                    {!zeile.unavailable &&
                      (zeile.adjusted || (gekuerzt[zeile.variantId] ?? 0) > zeile.quantity) && (
                        <span className="feld-hinweis">
                          {t('korb.gekuerzt', { anzahl: zeile.quantity })}
                        </span>
                      )}
                    {zeile.unavailable && (
                      <span className="feld-fehler" role="alert">
                        {t('kasse.zeileWeg')}
                      </span>
                    )}
                  </span>
                  <span>{zeile.unavailable ? '—' : preis(zeile.totalGross)}</span>
                </li>
              ))}
            </ul>

            {/* Alle Zahlen kommen aus derselben Antwort. Eine hier nachgerechnete
                Summe wäre eine zweite Wahrheit — und die abweichende fiele erst
                auf der Rechnung auf. */}
            <p className="summe">
              <span>{t('korb.zwischensumme')}</span>
              <strong>{preis(ansicht.subtotalGross)}</strong>
            </p>
            {ansicht.discountGross > 0 && (
              <p className="summe summe-rabatt">
                <span>{ansicht.discountName || t('rabatt.gewaehrt')}</span>
                <strong>−{preis(ansicht.discountGross)}</strong>
              </p>
            )}
            <p className="summe">
              <span>{t('kasse.versand')}</span>
              <strong>
                {versand
                  ? ansicht.shippingGross > 0
                    ? preis(ansicht.shippingGross)
                    : t('kasse.versandKostenlos')
                  : t('kasse.versandEntfaellt')}
              </strong>
            </p>
            {/* Der Freigrenzen-Hinweis bleibt in der Übersicht stehen: Er ist
                die wirksamste Zeile im Laden, und er wirkt nur, solange der
                Kunde noch etwas dazulegen kann. */}
            {ansicht.freeShippingMissing > 0 && (
              <p className="hinweis" role="status">
                {t('korb.freigrenze', { betrag: preis(ansicht.freeShippingMissing) })}
              </p>
            )}
            <p className="summe summe-gesamt">
              <span>{t('kasse.gesamt')}</span>
              <strong>{preis(ansicht.totalGross)}</strong>
            </p>
            <p className="preis-hinweis">
              {t('kasse.preishinweis')}{' '}
              {versand ? t('kasse.preishinweisVersand') : t('kasse.preishinweisAbholung')}
            </p>

            {/* Auch an der Kasse: Wer den Code erst hier aus der Mail kramt,
                soll nicht in den Warenkorb zurückmüssen. */}
            <Rabattfeld />

            {/* Die Sätze des Servers unverändert: Er kennt den Mindestbestellwert
                und die Lieferländer dieses Ladens, das Ladenfenster nicht. Sie
                stehen bei den Zahlen und nicht beim letzten Schritt — sie gelten
                vom ersten an, und wer sie erst am Ende läse, hätte umsonst
                getippt. */}
            {ansicht.blockers.map((satz) => (
              <p className="hinweis hinweis-fehler" role="alert" key={satz}>
                {satz}
              </p>
            ))}

            {unbestellbar && (
              <p className="hinweis hinweis-fehler" role="alert">
                {t('kasse.artikelWeg')}
              </p>
            )}
          </div>
        </aside>

        <form className="kasse" onSubmit={bestellen} noValidate>
          {/* Die Schrittanzeige sagt zweierlei: wo der Kunde steht und wie viel
              noch kommt. Das Zweite ist das Wichtigere — eine Kasse ohne
              absehbares Ende bricht man ab. */}
          <ol className="schrittanzeige" aria-label={t('kasse.schritteAria')}>
            {schritte.map((name, nummer) => {
              const zustand = nummer < index ? 'erledigt' : nummer === index ? 'aktuell' : 'offen'
              const beschriftung = t(SCHRITTNAME[name])
              return (
                <li
                  className="schrittanzeige-schritt"
                  key={name}
                  data-zustand={zustand}
                  aria-current={zustand === 'aktuell' ? 'step' : undefined}
                >
                  {/* Erledigte Schritte sind anklickbar, kommende nicht: Zurück
                      darf jeder jederzeit, vorwärts nur durch die Prüfung. Ein
                      gesperrter Knopf, der nach einem Verweis aussieht, wäre
                      eine Einladung, die niemand annehmen kann. */}
                  {zustand === 'erledigt' ? (
                    <button
                      className="schrittanzeige-knopf"
                      type="button"
                      onClick={() => setSchritt(name)}
                      aria-label={t('kasse.zurueckZu', { schritt: beschriftung })}
                    >
                      <span className="schrittanzeige-zahl" aria-hidden="true">
                        {nummer + 1}
                      </span>
                      {beschriftung}
                    </button>
                  ) : (
                    <span className="schrittanzeige-knopf">
                      <span className="schrittanzeige-zahl" aria-hidden="true">
                        {nummer + 1}
                      </span>
                      {beschriftung}
                    </span>
                  )}
                  {/* Blass gegenüber kräftig ist für eine Sprachausgabe kein
                      Unterschied — deshalb steht der Zustand auch in Worten. */}
                  <span className="nur-fuer-screenreader">
                    {zustand === 'erledigt'
                      ? t('kasse.schrittErledigt')
                      : zustand === 'aktuell'
                        ? t('kasse.schrittAktuell')
                        : t('kasse.schrittOffen')}
                  </span>
                </li>
              )
            })}
          </ol>

          <p className="feld-hinweis kasse-zaehler">
            {t('kasse.schrittZaehler', { nummer: index + 1, anzahl: schritte.length })}
          </p>

          {aktiv === 'kontakt' && (
            <section className="kasse-block" aria-labelledby="block-kontakt">
              <h2 id="block-kontakt">{t('kasse.kontakt')}</h2>
              <p className="beschreibung">
                {ich.signedIn ? t('kasse.kontaktAngemeldet') : t('kasse.kontaktGast')}
              </p>

              <Field
                id="email"
                label={t('kasse.email')}
                typ="email"
                inputMode="email"
                autoComplete="email"
                pflicht
                wert={formular.email}
                onWert={(wert) => setzeFeld('email', wert)}
                onBlur={() => beruehre('email')}
                fehler={sichtbar('email')}
                hinweis={t('kasse.emailHinweis')}
              />
              <Field
                id="name"
                label={t('kasse.name')}
                autoComplete="name"
                pflicht
                wert={formular.name}
                onWert={(wert) => setzeFeld('name', wert)}
                onBlur={() => beruehre('name')}
                fehler={sichtbar('name')}
              />
              <Field
                id="phone"
                label={t('kasse.telefon')}
                typ="tel"
                inputMode="tel"
                autoComplete="tel"
                wert={formular.phone}
                onWert={(wert) => setzeFeld('phone', wert)}
                onBlur={() => beruehre('phone')}
                fehler={sichtbar('phone')}
                hinweis={t('kasse.telefonHinweis')}
              />

            </section>
          )}

          {/* Die Wahl steht im ersten Schritt, weil sie entscheidet, was danach
              kommt: Bei Abholung entfällt der ganze Versandschritt. In einem
              eigenen Abschnitt und nicht unter derselben Überschrift — eine
              zweite gleich große Überschrift mitten im Block sieht aus wie ein
              vergessener Umbruch. */}
          {aktiv === 'kontakt' && (
            <section className="kasse-block" aria-labelledby="block-zustellung">
              <h2 id="block-zustellung">{t('kasse.zustellung')}</h2>
              {/* Die Wahl geht an den Warenkorb und nicht in dieses Formular:
                  Versandkosten, Freigrenze und Hinderungsgründe hängen daran,
                  und die rechnet der Server. */}
              <Wahlgruppe
                wert={zustellart}
                onWert={(neu) => setzeZustellung(neu as Fulfilment, land)}
                label="block-zustellung"
              >
                {(
                  [
                    { wert: 'shipping', titel: t('kasse.versand'), text: t('kasse.versandText') },
                    { wert: 'pickup', titel: t('kasse.abholung'), text: t('kasse.abholungText') },
                  ] as { wert: Fulfilment; titel: string; text: string }[]
                ).map((art) => (
                  <Wahl key={art.wert} wert={art.wert} titel={art.titel} text={art.text} />
                ))}
              </Wahlgruppe>
            </section>
          )}

          {aktiv === 'versand' && (
            <section className="kasse-block" aria-labelledby="block-adresse">
              <h2 id="block-adresse">{t('kasse.lieferadresse')}</h2>

              <Field
                id="empfaenger"
                label={t('kasse.empfaenger')}
                autoComplete="shipping name"
                wert={formular.empfaenger}
                onWert={(wert) => setzeFeld('empfaenger', wert)}
                onBlur={() => beruehre('empfaenger')}
                fehler={sichtbar('empfaenger')}
                hinweis={t('kasse.empfaengerHinweis', {
                  name: formular.name.trim() || t('kasse.sieSelbst'),
                })}
              />
              <Field
                id="firma"
                label={t('kasse.firma')}
                autoComplete="organization"
                wert={formular.firma}
                onWert={(wert) => setzeFeld('firma', wert)}
                onBlur={() => beruehre('firma')}
                fehler={sichtbar('firma')}
              />
              <Field
                id="strasse"
                label={t('kasse.strasse')}
                autoComplete="street-address"
                pflicht
                wert={formular.strasse}
                onWert={(wert) => setzeFeld('strasse', wert)}
                onBlur={() => beruehre('strasse')}
                fehler={sichtbar('strasse')}
              />
              <Field
                id="zusatz"
                label={t('kasse.zusatz')}
                wert={formular.zusatz}
                onWert={(wert) => setzeFeld('zusatz', wert)}
                onBlur={() => beruehre('zusatz')}
                fehler={sichtbar('zusatz')}
                hinweis={t('kasse.zusatzHinweis')}
              />

              <div className="feldpaar">
                <Field
                  id="plz"
                  label={t('kasse.plz')}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  pflicht
                  wert={formular.plz}
                  onWert={(wert) => setzeFeld('plz', wert)}
                  onBlur={() => beruehre('plz')}
                  fehler={sichtbar('plz')}
                />
                <Field
                  id="ort"
                  label={t('kasse.ort')}
                  autoComplete="address-level2"
                  pflicht
                  wert={formular.ort}
                  onWert={(wert) => setzeFeld('ort', wert)}
                  onBlur={() => beruehre('ort')}
                  fehler={sichtbar('ort')}
                />
              </div>

              <p className="feld">
                <label htmlFor="land">{t('kasse.land')}</label>
                <select
                  id="land"
                  value={land}
                  autoComplete="country"
                  /* Auch das Land geht an den Warenkorb: Ob der Laden dorthin
                     liefert, entscheidet der Server — und sagt es als Satz. */
                  onChange={(ereignis) => setzeZustellung(zustellart, ereignis.target.value)}
                >
                  {LAENDER.map((eintrag) => (
                    <option key={eintrag.code} value={eintrag.code}>
                      {t(eintrag.name)}
                    </option>
                  ))}
                </select>
              </p>
            </section>
          )}

          {aktiv === 'zahlung' && (
            <>
              {/* Der Rückblick auf das Eingegebene.
                  Gleich darunter steht „Zahlungspflichtig bestellen", und
                  danach ist die Bestellung verbindlich. Wer bis hierher
                  gekommen ist, hat die Anschrift zwei Schritte vorher getippt
                  — sie noch einmal zu lesen, ohne zurückblättern zu müssen,
                  ist der Unterschied zwischen einem Tippfehler und einem Paket,
                  das an die alte Wohnung geht.

                  Die Verweise springen in den Schritt und nicht in ein
                  Eingabefeld: Wer die Anschrift ändern will, will sie ganz
                  sehen, nicht in der Mitte eines Formulars stehen. */}
              <section className="kasse-block kasse-rueckblick" aria-labelledby="block-angaben">
                <h2 id="block-angaben">{t('kasse.angaben')}</h2>

                <div className="rueckblick-teil">
                  <div className="rueckblick-kopf">
                    <h3>{t('kasse.kontakt')}</h3>
                    <button
                      type="button"
                      className="verweisknopf"
                      onClick={() => setSchritt('kontakt')}
                      aria-label={t('kasse.angabenAendern', { was: t('kasse.kontakt') })}
                    >
                      {t('kasse.aendern')}
                    </button>
                  </div>
                  <p className="rueckblick-zeilen">
                    {formular.email}
                    {formular.name && <><br />{formular.name}</>}
                    {formular.phone && <><br />{formular.phone}</>}
                  </p>
                </div>

                <div className="rueckblick-teil">
                  <div className="rueckblick-kopf">
                    <h3>{t('kasse.angabenLieferung')}</h3>
                    {versand && (
                      <button
                        type="button"
                        className="verweisknopf"
                        onClick={() => setSchritt('versand')}
                        aria-label={t('kasse.angabenAendern', {
                          was: t('kasse.angabenLieferung'),
                        })}
                      >
                        {t('kasse.aendern')}
                      </button>
                    )}
                  </div>
                  {versand ? (
                    <p className="rueckblick-zeilen">
                      {/* Der abweichende Empfänger zuerst, sonst der Besteller:
                          Steht dort ein anderer Name, gehört er nach oben —
                          sonst liest sich die Anschrift, als ginge das Paket an
                          jemand anderen als vereinbart. */}
                      {formular.empfaenger || formular.name}
                      {formular.firma && <><br />{formular.firma}</>}
                      <br />
                      {formular.strasse}
                      {formular.zusatz && <><br />{formular.zusatz}</>}
                      <br />
                      {formular.plz} {formular.ort}
                      <br />
                      {landName(land)}
                    </p>
                  ) : (
                    <p className="rueckblick-zeilen">{t('kasse.angabenAbholung')}</p>
                  )}
                </div>
              </section>

              {/* Die Zahlungsart wird hier gewählt, angefordert wird sie erst
                  nach dem Bestellen: Vorher gibt es keine Bestellung, an der ein
                  Betrag hinge. Die Wahl reist mit auf die Bezahlseite. */}
              {arten && arten.length > 0 && (
                <section className="kasse-block" aria-labelledby="block-zahlung">
                  <h2 id="block-zahlung">{t('kasse.zahlungsart')}</h2>

                  <Wahlgruppe
                    wert={zahlungsart}
                    onWert={setZahlungsart}
                    label="block-zahlung"
                  >
                    {arten.map((art) => (
                      /* Der Hinweis kommt vom Server, unverändert: Er sagt, was
                         nach dem Bestellen passiert. */
                      <Wahl key={art.key} wert={art.key} titel={art.label} text={art.hint} />
                    ))}
                  </Wahlgruppe>
                </section>
              )}

              {/* Der Gutschein steht NEBEN den Zahlungsarten, nicht darin.
                  
                  Er war erst darin geschachtelt — und damit verschwand er
                  mitsamt dem Block, wenn der Laden keine Zahlungsart
                  hinterlegt hat. Ausgerechnet dann ist ein Gutschein aber das
                  Einzige, womit sich überhaupt zahlen ließe. */}
              <section className="kasse-block" aria-labelledby="block-gutschein">
                <h2 id="block-gutschein">{t('gutschein.titel')}</h2>
                <Gutscheinfeld
                  gutscheine={gutscheine}
                  onGutscheine={setGutscheine}
                  offenerBetrag={ansicht.totalGross}
                />
              </section>

              <section className="kasse-block" aria-labelledby="block-anmerkung">
                <h2 id="block-anmerkung">{t('kasse.anmerkung')}</h2>
                <p className="feld">
                  <label htmlFor="note">
                    {t('kasse.nachricht')}
                    <span className="feld-zusatz"> {t('feld.freiwillig')}</span>
                  </label>
                  <textarea
                    id="note"
                    rows={3}
                    value={formular.note}
                    onChange={(ereignis) => setzeFeld('note', ereignis.target.value)}
                  />
                  <span className="feld-hinweis">{t('kasse.nachrichtHinweis')}</span>
                </p>
              </section>

              {serverfehler && (
                <p className="hinweis hinweis-fehler" role="alert">
                  {serverfehler}
                </p>
              )}

              {/*
                Die Widerrufsbelehrung vor dem Bestellknopf, nicht nur im Fuß.
                Art. 246a EGBGB verlangt, dass der Kunde vor Abgabe seiner
                Bestellung über sein Widerrufsrecht unterrichtet wird; fehlt die
                Belehrung, verlängert sich die Frist von vierzehn Tagen auf ein
                Jahr und vierzehn Tage. Der Verweis steht nur da, wenn der Laden
                die Seite veröffentlicht hat — ein Verweis ins Leere wäre
                schlimmer als keiner.

                In einem neuen Fenster: Ein Wechsel innerhalb der Seite würfe das
                ausgefüllte Formular weg, und wer danach alles noch einmal tippen
                soll, liest die Belehrung beim nächsten Mal nicht mehr.
              */}
              {widerruf && (
                <p className="preis-hinweis">
                  {t('kasse.widerrufVor')}{' '}
                  <a href={`/seite/${widerruf.handle}`} target="_blank" rel="noopener noreferrer">
                    {widerruf.title}
                  </a>
                  {t('kasse.widerrufNach')}
                </p>
              )}
            </>
          )}

          {versucht.has(aktiv) && offeneFelder(aktiv).length > 0 && (
            <p className="hinweis hinweis-fehler" role="alert">
              {t('kasse.bitteErgaenzen')}
            </p>
          )}

          <div className="kasse-fuss">
            {index > 0 && (
              <button
                className="knopf knopf-schlicht"
                type="button"
                onClick={zurueck}
                aria-label={t('kasse.zurueckZu', { schritt: t(SCHRITTNAME[schritte[index - 1]!]) })}
              >
                {t('kasse.zurueck')}
              </button>
            )}

            {letzter ? (
              <button
                className="knopf knopf-gross"
                type="submit"
                /* Der zweite Klick während der Anfrage darf nichts auslösen — die
                   Sperre steht doppelt: hier sichtbar, in `laeuft` wirksam. Und
                   solange der Warenkorb sich gerade ändert, stimmen die Zahlen
                   daneben noch nicht. */
                disabled={sendet || arbeitet || gesperrt}
              >
                {sendet ? t('kasse.bestelltGerade') : t('kasse.bestellen')}
              </button>
            ) : (
              <button
                className="knopf knopf-gross"
                type="button"
                onClick={weiter}
                aria-label={t('kasse.weiterZu', { schritt: t(SCHRITTNAME[schritte[index + 1]!]) })}
              >
                {t('kasse.weiter')}
              </button>
            )}
          </div>

          {letzter && <p className="preis-hinweis">{t('kasse.schlusshinweis')}</p>}
        </form>
      </div>
    </>
  )
}
